import { FormControl, FormGroup, Validators } from '@angular/forms';

import { FormHelper } from 'tiime-expert-utils';

import { AntiMoneyLaundering } from '@common/models/anti-money-laundering';
import { Level } from '@enums/level.enum';
import { ToFormGroupType } from '@modules/core/types/to-form-group';

export type AntiMoneyLaunderingFormType = ToFormGroupType<Required<AntiMoneyLaundering>>;

export class AntiMoneyLaunderingForm extends FormGroup<AntiMoneyLaunderingFormType> {
  constructor(questionRequired: boolean = false) {
    super({
      id: new FormControl<number | undefined>(undefined),
      ape: new FormControl<string | null>(null),
      identityConclusion: new FormControl<Level | null>(null),
      identityRisk: new FormControl<Level | null>(null),
      shareholdingConclusion: new FormControl<Level | null>(null),
      shareholdingRisk: new FormControl<Level | null>(null),
      operationsConclusion: new FormControl<Level | null>(null),
      operationsRisk: new FormControl<Level | null>(null),
      globalConclusion: new FormControl<Level | null>(null),
      globalRisk: new FormControl<Level | null>(null),
      entityIdentificationDocumentation: new FormControl<boolean | null>(
        null,
        questionRequired ? Validators.required : null
      ),
      highRiskActivitySector: new FormControl<boolean | null>(null, questionRequired ? Validators.required : null),
      frequentLegalChanges: new FormControl<boolean | null>(null, questionRequired ? Validators.required : null),
      effectiveBeneficiaryIdentified: new FormControl<boolean | null>(
        null,
        questionRequired ? Validators.required : null
      ),
      effectiveBeneficiaryIdentityPiece: new FormControl<boolean | null>(
        null,
        questionRequired ? Validators.required : null
      ),
      directorsPoliticallyExposed: new FormControl<boolean | null>(null, questionRequired ? Validators.required : null),
      directorsIntegrityDoubts: new FormControl<boolean | null>(null, questionRequired ? Validators.required : null),
      periodOperationsConsistent: new FormControl<boolean | null>(null, questionRequired ? Validators.required : null),
      complexOperations: new FormControl<boolean | null>(null, questionRequired ? Validators.required : null),
      irregularHighAmounts: new FormControl<boolean | null>(null, questionRequired ? Validators.required : null),
      operationsWithoutEconomicProofs: new FormControl<boolean | null>(
        null,
        questionRequired ? Validators.required : null
      ),
      fundsOriginsJustified: new FormControl<boolean | null>(null, questionRequired ? Validators.required : null),
      riskedTransactions: new FormControl<boolean | null>(null, questionRequired ? Validators.required : null),
      representantIdentificationValid: new FormControl<boolean | null>(
        null,
        questionRequired ? Validators.required : null
      )
    });
  }

  fromAntiMoneyLaundering(antiMoneyLaundering: AntiMoneyLaundering): void {
    this.patchValue({
      id: antiMoneyLaundering?.id ?? null,
      ape: antiMoneyLaundering?.ape ?? null,
      identityConclusion: antiMoneyLaundering?.identityConclusion ?? null,
      identityRisk: antiMoneyLaundering?.identityRisk ?? null,
      shareholdingConclusion: antiMoneyLaundering?.shareholdingConclusion ?? null,
      shareholdingRisk: antiMoneyLaundering?.shareholdingRisk ?? null,
      operationsConclusion: antiMoneyLaundering?.operationsConclusion ?? null,
      operationsRisk: antiMoneyLaundering?.operationsRisk ?? null,
      globalConclusion: antiMoneyLaundering?.globalConclusion ?? null,
      globalRisk: antiMoneyLaundering?.globalRisk ?? null,
      entityIdentificationDocumentation: antiMoneyLaundering?.entityIdentificationDocumentation ?? null,
      highRiskActivitySector: antiMoneyLaundering?.highRiskActivitySector ?? null,
      frequentLegalChanges: antiMoneyLaundering?.frequentLegalChanges ?? null,
      effectiveBeneficiaryIdentified: antiMoneyLaundering?.effectiveBeneficiaryIdentified ?? null,
      effectiveBeneficiaryIdentityPiece: antiMoneyLaundering?.effectiveBeneficiaryIdentityPiece ?? null,
      directorsPoliticallyExposed: antiMoneyLaundering?.directorsPoliticallyExposed ?? null,
      directorsIntegrityDoubts: antiMoneyLaundering?.directorsIntegrityDoubts ?? null,
      periodOperationsConsistent: antiMoneyLaundering?.periodOperationsConsistent ?? null,
      complexOperations: antiMoneyLaundering?.complexOperations ?? null,
      irregularHighAmounts: antiMoneyLaundering?.irregularHighAmounts ?? null,
      operationsWithoutEconomicProofs: antiMoneyLaundering?.operationsWithoutEconomicProofs ?? null,
      fundsOriginsJustified: antiMoneyLaundering?.fundsOriginsJustified ?? null,
      riskedTransactions: antiMoneyLaundering?.riskedTransactions ?? null,
      representantIdentificationValid: antiMoneyLaundering?.representantIdentificationValid ?? null
    });
  }

  toAntiMoneyLaundering(): AntiMoneyLaundering {
    const antiMoneyLaundering = new AntiMoneyLaundering();

    antiMoneyLaundering.id = this.controls.id.value;
    antiMoneyLaundering.ape = this.controls.ape.value;
    antiMoneyLaundering.identityConclusion = this.controls.identityConclusion.value;
    antiMoneyLaundering.identityRisk = this.controls.identityRisk.value;
    antiMoneyLaundering.shareholdingConclusion = this.controls.shareholdingConclusion.value;
    antiMoneyLaundering.shareholdingRisk = this.controls.shareholdingRisk.value;
    antiMoneyLaundering.operationsConclusion = this.controls.operationsConclusion.value;
    antiMoneyLaundering.operationsRisk = this.controls.operationsRisk.value;
    antiMoneyLaundering.globalConclusion = this.controls.globalConclusion.value;
    antiMoneyLaundering.globalRisk = this.controls.globalRisk.value;
    antiMoneyLaundering.entityIdentificationDocumentation = this.controls.entityIdentificationDocumentation.value;
    antiMoneyLaundering.highRiskActivitySector = this.controls.highRiskActivitySector.value;
    antiMoneyLaundering.frequentLegalChanges = this.controls.frequentLegalChanges.value;
    antiMoneyLaundering.effectiveBeneficiaryIdentified = this.controls.effectiveBeneficiaryIdentified.value;
    antiMoneyLaundering.effectiveBeneficiaryIdentityPiece = this.controls.effectiveBeneficiaryIdentityPiece.value;
    antiMoneyLaundering.directorsPoliticallyExposed = this.controls.directorsPoliticallyExposed.value;
    antiMoneyLaundering.directorsIntegrityDoubts = this.controls.directorsIntegrityDoubts.value;
    antiMoneyLaundering.periodOperationsConsistent = this.controls.periodOperationsConsistent.value;
    antiMoneyLaundering.complexOperations = this.controls.complexOperations.value;
    antiMoneyLaundering.irregularHighAmounts = this.controls.irregularHighAmounts.value;
    antiMoneyLaundering.operationsWithoutEconomicProofs = this.controls.operationsWithoutEconomicProofs.value;
    antiMoneyLaundering.fundsOriginsJustified = this.controls.fundsOriginsJustified.value;
    antiMoneyLaundering.riskedTransactions = this.controls.riskedTransactions.value;
    antiMoneyLaundering.representantIdentificationValid = this.controls.representantIdentificationValid.value;

    return antiMoneyLaundering;
  }

  setConclusions(
    antiMoneyLaundering: Pick<
      AntiMoneyLaundering,
      'globalConclusion' | 'identityConclusion' | 'operationsConclusion' | 'shareholdingConclusion'
    >
  ): void {
    this.patchValue(
      {
        globalConclusion: antiMoneyLaundering.globalConclusion,
        identityConclusion: antiMoneyLaundering.identityConclusion,
        operationsConclusion: antiMoneyLaundering.operationsConclusion,
        shareholdingConclusion: antiMoneyLaundering.shareholdingConclusion
      },
      FormHelper.shouldNotEmitEvent
    );
  }
}
